import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { logService } from "../utils/logService";

const History = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isModerator, userRole } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch agent details first
        const { db } = await import("../firebase");
        const { doc, getDoc } = await import("firebase/firestore");

        const agentRef = doc(db, "agents", id);
        const agentSnap = await getDoc(agentRef);

        if (agentSnap.exists()) {
          const agentData = agentSnap.data();

          // For public access, we don't check permissions
          // Users can view any agent's history

          setAgent({ id: agentSnap.id, ...agentData });

          // Fetch all logs and filter by agent
          const logsData = await logService.getAllLogs(200);
          const agentLogs = logsData.filter(
            (log) =>
              log.details &&
              (log.details.agentId === id ||
                (log.action === "Agent Created" &&
                  log.details.agentId === id) ||
                (log.action === "Agent Status Changed" &&
                  log.details.agentId === id))
          );
          setLogs(agentLogs);
        } else {
          navigate("/agents");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        navigate("/agents");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, navigate]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "Agent Created":
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
        );
      case "Agent Status Changed":
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
            />
          </svg>
        );
      case "Validation Added":
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case "User Logged In":
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
            />
          </svg>
        );
      case "User Registered":
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            />
          </svg>
        );
      case "User Added by Admin":
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        );
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "Agent Created":
        return "bg-green-600";
      case "Agent Status Changed":
        return "bg-blue-600";
      case "Validation Added":
        return "bg-purple-600";
      case "User Logged In":
        return "bg-indigo-600";
      case "User Registered":
        return "bg-teal-600";
      case "User Added by Admin":
        return "bg-orange-600";
      default:
        return "bg-gray-600";
    }
  };

  const filteredLogs = logs; // Show all logs since we removed filtering

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">
            Loading history...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-valia-primary mb-2">
              {agent
                ? `${agent["Agent Name"] || "Unnamed Agent"} History`
                : "Agent History"}
            </h1>
            {agent && (
              <div className="text-valia-gray-600 text-base">
                <span className="font-medium text-valia-primary">
                  {agent["Agent Name"] || "Unnamed Agent"}
                </span>
                <span className="mx-2">•</span>
                <span>{agent["Agent Type"] || "Unknown Type"}</span>
                <span className="mx-2">•</span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    agent["Verification Status"] === "Verified"
                      ? "valia-badge-success border-green-300"
                      : agent["Verification Status"] === "Pending"
                      ? "valia-badge-warning border-orange-300"
                      : agent["Verification Status"] === "Under Review"
                      ? "valia-badge-info border-blue-300"
                      : agent["Verification Status"] === "Unverified"
                      ? "valia-badge-error border-red-300"
                      : "valia-badge-gray border-gray-400"
                  }`}
                >
                  {agent["Verification Status"] || "Unknown"}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate("/agents")}
            className="valia-btn-secondary valia-btn-sm flex items-center gap-2 self-start sm:self-auto"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Agents
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {filteredLogs.length === 0 ? (
          <div className="valia-card text-center py-12">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 text-valia-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-valia-gray-600 text-lg mb-4">
              No history found for this agent.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Main timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-valia-primary via-valia-accent to-valia-success"></div>

            <div className="space-y-6">
              {filteredLogs.map((log, index) => {
                const timestamp = formatTimestamp(log.timestamp);
                const isLast = index === filteredLogs.length - 1;

                return (
                  <div key={log.id} className="relative">
                    {/* Timeline item */}
                    <div className="flex items-start gap-6">
                      {/* Timeline dot */}
                      <div className="relative flex-shrink-0">
                        <div
                          className={`w-16 h-16 rounded-full ${getActionColor(
                            log.action
                          )} flex items-center justify-center shadow-valia-lg border-4 border-valia-background-secondary relative z-10`}
                        >
                          {getActionIcon(log.action)}
                        </div>
                        {/* Pulse animation for latest item */}
                        {index === 0 && (
                          <div
                            className={`absolute inset-0 w-16 h-16 rounded-full ${getActionColor(
                              log.action
                            )} animate-ping opacity-20`}
                          ></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pb-6">
                        <div className="valia-card hover:shadow-valia-lg transition-all duration-300 ease-in-out p-6">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-valia-primary mb-2">
                                {log.action}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-valia-gray-600">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                                <span>Performed by</span>
                                <span className="font-semibold text-valia-accent">
                                  {log.created_by_name}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="bg-valia-gray-50 rounded-valia px-3 py-2 border border-valia-gray-200">
                                <div className="text-valia-gray-700 text-sm font-semibold">
                                  {timestamp.date}
                                </div>
                                <div className="text-valia-gray-500 text-xs">
                                  {timestamp.time}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Details */}
                          {log.details &&
                            Object.keys(log.details).length > 0 && (
                              <div className="bg-valia-gray-50 rounded-valia p-4 border border-valia-gray-200">
                                <h4 className="text-sm font-semibold text-valia-gray-700 mb-3 flex items-center gap-2">
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  Activity Details
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {Object.entries(log.details).map(
                                    ([key, value]) => (
                                      <div
                                        key={key}
                                        className="flex justify-between items-center text-sm bg-white rounded-valia px-3 py-2 border border-valia-gray-200"
                                      >
                                        <span className="text-valia-gray-600 font-medium capitalize">
                                          {key
                                            .replace(/([A-Z])/g, " $1")
                                            .trim()}
                                          :
                                        </span>
                                        <span
                                          className="text-valia-gray-900 text-right max-w-xs truncate font-medium"
                                          title={String(value)}
                                        >
                                          {String(value)}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
